import React from "react";
import clsx from "clsx";
import {
  TableRow,
  TableCell,
  Typography,
  IconButton,
  Link,
  Avatar,
} from "@mui/material";
import {
  ArrowForwardRounded,
  DeleteForever as DeleteForeverIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";
import { ITutorialJourneyProfileDto } from "@growth-machine-llc/stridist-api-client";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { ADMIN_USERS_ROUTE } from "../../../routes/routes";

const useStyles = makeStyles((theme) => ({
  link: {
    textDecoration: "underline",
    color: theme.palette.text.primary,
    "&:hover": {
      textDecoration: "none",
      color: theme.palette.text.secondary,
    },
  },
  value: {
    fontSize: 14,
    fontWeight: 500,
  },
  description: {
    fontSize: 14,
    maxWidth: 200,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

export interface ProfileTableRowProps {
  profile: ITutorialJourneyProfileDto;
  onForwardClick: (
    e: React.MouseEvent<HTMLButtonElement>,
    profile: ITutorialJourneyProfileDto,
  ) => void;
  onDeleteClick: (
    e: React.MouseEvent<HTMLButtonElement>,
    profile: ITutorialJourneyProfileDto,
  ) => void;
  onEditClick: (profile: ITutorialJourneyProfileDto) => void;
}

export function ProfileTableRow({
  profile,
  onForwardClick,
  onDeleteClick,
  onEditClick,
}: ProfileTableRowProps) {
  const s = useStyles();

  return (
    <TableRow>
      <TableCell>
        <Avatar src={profile.avatar ?? undefined} alt={profile.name} />
      </TableCell>

      <TableCell>
        <Typography className={s.value}>{profile.name}</Typography>
      </TableCell>

      <TableCell align="center">
        <Typography className={s.description}>
          {profile.description || "-"}
        </Typography>
      </TableCell>

      <TableCell align="center">
        {profile.createdBy ? (
          <Link
            href={`${ADMIN_USERS_ROUTE}/${profile.createdBy}`}
            target="_blank"
            rel="noopener noreferrer"
            className={clsx(s.link)}
          >
            <Typography className={clsx(s.value)} component="span">
              {profile.createdBy}
            </Typography>
            <ArrowOutwardIcon fontSize="small" style={{ marginLeft: 4 }} />
          </Link>
        ) : (
          "-"
        )}
      </TableCell>

      <TableCell align="center">
        <Typography variant="caption">
          <b>Created:</b>{" "}
          {profile.created ? profile.created.format("MMM D, YYYY") : "-"}
        </Typography>
        <br />
        <Typography variant="caption">
          <b>Updated:</b>{" "}
          {profile.lastModified
            ? profile.lastModified.format("MMM D, YYYY")
            : "-"}
        </Typography>
      </TableCell>

      <TableCell align="center">
        {profile.lastModifiedBy ? (
          <Link
            href={`${ADMIN_USERS_ROUTE}/${profile.lastModifiedBy}`}
            target="_blank"
            rel="noopener noreferrer"
            className={s.link}
          >
            <Typography className={s.value} component="span">
              {profile.lastModifiedBy}
            </Typography>
            <ArrowOutwardIcon fontSize="small" style={{ marginLeft: 4 }} />
          </Link>
        ) : (
          "-"
        )}
      </TableCell>

      <TableCell align="center">
        <IconButton onClick={() => onEditClick(profile)} size="small">
          <EditIcon />
        </IconButton>
        <IconButton onClick={(e) => onForwardClick(e, profile)} size="large">
          <ArrowForwardRounded />
        </IconButton>
        <IconButton onClick={(e) => onDeleteClick(e, profile)}>
          <DeleteForeverIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}
